import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePickemLeaderboard, useUserPickemPicks } from '@/api/query/useLeaderboard';
import { formatPrizeLabel, type UserPrizeData } from '@/utils/prize';
import { getLocalizedField } from '@/utils/localized-field';
import { QuestionPickRow } from './QuestionPickRow';
import { LeaderboardTable, type LeaderboardTableEntry } from './LeaderboardTable';

interface PickemLeaderboardProps {
  pickemId: string;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
  /**
   * Contest start time. When provided and in the future, other users'
   * picks are hidden (chevron suppressed) so the leaderboard does not
   * spoil selections pre-kickoff. The current user can always view
   * their own picks.
   */
  contestStartUtc?: string | null;
}

const ExpandedPicks: React.FC<{
  pickemId: string;
  userId: string;
  isCurrentUser: boolean;
  prize?: UserPrizeData | null;
}> = ({ pickemId, userId, isCurrentUser, prize }) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useUserPickemPicks(pickemId, userId);

  if (isLoading) {
    return (
      <div
        className={`px-2 py-3 text-sm font-sans ${
          isCurrentUser ? 'bg-white text-background/70' : 'bg-transparent text-muted-foreground'
        }`}
      >
        {t('leaderboard.picksLoading')}
      </div>
    );
  }

  if (error || !data?.picks || data.picks.length === 0) {
    return (
      <div
        className={`px-2 py-3 text-sm font-sans ${
          isCurrentUser ? 'bg-white text-background/70' : 'bg-transparent text-muted-foreground'
        }`}
      >
        {t('leaderboard.noPicks')}
      </div>
    );
  }

  const prizeLabel = formatPrizeLabel(prize);
  const hasExtraPrizeDetails =
    prize && ((prize.crowns && prize.crowns > 0) || (prize.cash && prize.cash > 0) || prize.other);

  return (
    <div
      className={`px-2 pb-3 border-b border-border ${isCurrentUser ? 'bg-white' : 'bg-surface-muted/40'}`}
    >
      <div className="pl-2">
        {data.picks.map((pick, index) => (
          <QuestionPickRow
            key={pick.question_id}
            questionNumber={index + 1}
            questionText={
              getLocalizedField(pick, 'question_text', i18n.language) || pick.question_text
            }
            optionText={getLocalizedField(pick, 'option_text', i18n.language) || pick.option_text}
            optionImage={pick.option_image}
            isCorrect={pick.is_correct}
            pointsEarned={pick.points_earned}
            isCurrentUser={isCurrentUser}
          />
        ))}
      </div>

      {hasExtraPrizeDetails && (
        <div className={`pt-3 border-t ${isCurrentUser ? 'border-black/15' : 'border-border'}`}>
          <div
            className={`text-xs font-sans font-medium mb-1 ${
              isCurrentUser ? 'text-background/70' : 'text-muted-foreground'
            }`}
          >
            {t('leaderboard.prizeDetails')}
          </div>
          <div className="flex flex-wrap gap-2">
            {prizeLabel && (
              <span
                className={`text-sm font-sans font-semibold ${
                  isCurrentUser ? 'text-background' : 'text-success'
                }`}
              >
                {prizeLabel}
              </span>
            )}
            {prize.crowns && prize.crowns > 0 && prize.cash && prize.cash > 0 && (
              <span
                className={`text-sm font-sans ${
                  isCurrentUser ? 'text-background/80' : 'text-muted-foreground'
                }`}
              >
                {prize.crowns.toLocaleString()} {t('leaderboard.crowns')}
              </span>
            )}
            {prize.other && prize.cash && prize.cash > 0 && (
              <span
                className={`text-sm font-sans ${
                  isCurrentUser ? 'text-background/80' : 'text-muted-foreground'
                }`}
              >
                {prize.other}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const PickemLeaderboard: React.FC<PickemLeaderboardProps> = ({
  pickemId,
  isOpen,
  onClose: _onClose,
  currentUserId,
  contestStartUtc,
}) => {
  const [now, setNow] = useState(() => Date.now());
  const startMs = contestStartUtc ? new Date(contestStartUtc).getTime() : null;
  const contestHasStarted = startMs === null || startMs <= now;

  useEffect(() => {
    if (startMs === null || startMs <= now) return;
    const delay = Math.min(startMs - now, 2_147_483_000);
    const t = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(t);
  }, [startMs, now]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePickemLeaderboard({
      pickemId,
    });

  if (!isOpen) return null;

  const entries: LeaderboardTableEntry[] =
    data?.pages.flatMap(page =>
      page.leaderboard.map(entry => ({
        user_id: entry.user_id,
        username: entry.username,
        points: entry.points,
        rank: entry.rank,
        prize: entry.prize as UserPrizeData | null | undefined,
      }))
    ) || [];

  return (
    <div data-test-id="pickem-leaderboard-overlay" className="pt-6">
      <LeaderboardTable
        entries={entries}
        currentUserId={currentUserId}
        isLoading={isLoading}
        error={!!error}
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        canExpandEntry={(_entry, isCurrentUser) => isCurrentUser || contestHasStarted}
        renderExpanded={(entry, isCurrentUser) => (
          <ExpandedPicks
            pickemId={pickemId}
            userId={entry.user_id}
            isCurrentUser={isCurrentUser}
            prize={entry.prize}
          />
        )}
      />
    </div>
  );
};

export default PickemLeaderboard;
